import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  clinicHolidaysApi,
  clinicHolidayQueryKeys,
} from "../../../shared/api/clinicHolidays";
import { apiError } from "../../../shared/api/client";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../../shared/ui/bulkSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Lịch nghỉ — specific days the clinic is closed (#18860).
 *
 * <p>Lives beside "Giờ làm việc" on the clinic settings page, because the two answer one question
 * on two axes: which weekdays are closed, and which individual dates are. They used to sit in
 * different settings groups, so a user had to visit two places to say "we are shut".
 *
 * <p>The online-booking switch that used to share this page moved to Đặt lịch — Giới hạn & nhắc
 * lịch: turning bookings off for a channel is not a statement about opening hours.
 */
export function HolidaysPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: clinicHolidayQueryKeys.all(),
    queryFn: () => clinicHolidaysApi.list(),
  });


  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);

  const add = useMutation({
    mutationFn: () => clinicHolidaysApi.add(date, reason || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicHolidayQueryKeys.all() });
      setDate("");
      setReason("");
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const remove = useMutation({
    mutationFn: (id: string) => clinicHolidaysApi.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: clinicHolidayQueryKeys.all() }),
  });

  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete(
      [...sel.selectedIds],
      clinicHolidaysApi.remove,
    );
    setBulkPending(false);
    sel.clear();
    qc.invalidateQueries({ queryKey: clinicHolidayQueryKeys.all() });
    setError(failed.length ? `${failed.length} ngày nghỉ không xoá được.` : null);
  }

  const holidays = q.data ?? [];


  return (
    <div className="grid gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="holiday-date">Ngày nghỉ *</Label>
            <Input
              id="holiday-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="min-w-[200px] flex-1 space-y-1.5">
            <Label htmlFor="holiday-reason">Lý do</Label>
            <Input
              id="holiday-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              placeholder="VD: Nghỉ Tết Nguyên đán, Quốc khánh 2/9…"
            />
          </div>
          <Button
            type="button"
            disabled={!date || add.isPending}
            onClick={() => {
              setError(null);
              add.mutate();
            }}
          >
            {add.isPending ? "Đang thêm…" : "Thêm ngày nghỉ"}
          </Button>
        </div>

        {error && <p className="m-0 text-sm text-destructive">{error}</p>}

        {q.isLoading && (
          <p className="text-sm text-muted-foreground">Đang tải…</p>
        )}
        {q.data && (
          <>
            <BulkDeleteBar
              count={sel.count}
              noun="ngày nghỉ"
              pending={bulkPending}
              onClear={sel.clear}
              onDelete={bulkDelete}
            />
            {holidays.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Chưa có ngày nghỉ nào.
              </p>
            ) : (
              <>
                <label className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    aria-label="Chọn tất cả"
                    className="h-4 w-4 rounded border-input accent-primary"
                    checked={
                      holidays.length > 0 &&
                      holidays.every((h) => sel.selectedIds.has(h.id))
                    }
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          holidays.some((h) => sel.selectedIds.has(h.id)) &&
                          !holidays.every((h) => sel.selectedIds.has(h.id));
                    }}
                    onChange={() => {
                      const next = new Set(sel.selectedIds);
                      if (holidays.every((h) => next.has(h.id)))
                        holidays.forEach((h) => next.delete(h.id));
                      else holidays.forEach((h) => next.add(h.id));
                      sel.setSelectedIds(next);
                    }}
                  />
                  Chọn tất cả
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {holidays.map((h) => (
                    <div
                      key={h.id}
                      className={`rounded-lg border bg-card p-3 ${
                        sel.selectedIds.has(h.id)
                          ? "border-primary/50 ring-1 ring-primary/30"
                          : ""
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <input
                          type="checkbox"
                          aria-label="Chọn dòng"
                          className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-primary"
                          checked={sel.selectedIds.has(h.id)}
                          onChange={() => sel.toggle(h.id)}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-foreground font-mono tabular-nums">
                            {new Date(h.date).toLocaleDateString("vi-VN", {
                              weekday: "short",
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Lý do: {h.reason ?? "—"}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-2.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="ml-auto text-destructive hover:text-destructive"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(h.id)}
                        >
                          Xoá
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
    </div>
  );
}
